import { memo } from "react";
import { cn } from "@/lib/utils";

interface ShowDateDividerProps {
	date: Date;
	type: "day" | "month";
	className?: string;
}

/**
 * Date divider for show lists
 * - Day dividers: "Thu Oct 25" (smaller, for daily grouping)
 * - Month dividers: "November 2025" (larger, for monthly grouping)
 * - Sticky behavior while scrolling
 */
export const ShowDateDivider = memo<ShowDateDividerProps>(
	({ date, type, className }) => {
		const formatDayDivider = (d: Date) => {
			// Format: "Thu Oct 25"
			const weekday = d.toLocaleDateString("en-US", { weekday: "short" });
			const month = d.toLocaleDateString("en-US", { month: "short" });
			const day = d.getDate();
			return `${weekday} ${month} ${day}`;
		};

		const formatMonthDivider = (d: Date) => {
			// Format: "November 2025"
			const month = d.toLocaleDateString("en-US", { month: "long" });
			const year = d.getFullYear();
			return `${month} ${year}`;
		};

		const displayText =
			type === "day" ? formatDayDivider(date) : formatMonthDivider(date);

		return (
			<div
				className={cn(
					"sticky top-14 z-20 -mx-3 px-3 py-1 backdrop-blur-sm bg-background/95",
					type === "day" && "text-foreground text-sm font-medium",
					type === "month" && "text-foreground text-lg font-semibold",
					className,
				)}
			>
				{displayText}
			</div>
		);
	},
);

ShowDateDivider.displayName = "ShowDateDivider";
